import { useTranslation } from 'react-i18next'
import { Dialog, Button, FormItem } from 'vnetwork-ui'
import { useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'

import { ViewReportType } from '../../types/payload'

interface ViewReportDialogProps {
    open: boolean
    reportId: number
    onConfirm: (payload: ViewReportType) => void
    onClose: () => void
    isLoading?: boolean
}

interface FormValues {
    date_range?: [string, string]
}

const ViewReportDialog: React.FC<ViewReportDialogProps> = ({
    open,
    reportId,
    onConfirm,
    onClose,
    isLoading = false,
}) => {
    const { t } = useTranslation('report_waf')

    const today = dayjs().format('YYYY-MM-DD')
    const { control, handleSubmit } = useForm<FormValues>({
        defaultValues: {
            date_range: [today, today],
        },
    })

    const onSubmit = (data: FormValues) => {
        if (!data.date_range || data.date_range.length !== 2) {
            toast.error(t('errors.date_required'))
            return
        }

        const start = dayjs(data.date_range[0])
        const end = dayjs(data.date_range[1])

        if (!start.isValid() || !end.isValid()) {
            toast.error(t('errors.invalid_date_range'))
            return
        }

        if (!start.isBefore(end)) {
            toast.error(t('errors.start_after_end'))
            return
        }

        const startDate = start.format('YYYY-MM-DD')
        const endDate = end.format('YYYY-MM-DD')

        const payload: ViewReportType = {
            id: reportId,
            start_date: startDate,
            end_date: endDate,
        }

        onConfirm(payload)
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('view_dialog.title')}
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="p-4 space-y-4">
                    <FormItem
                        control={control}
                        name="date_range"
                        type="daterangepicker"
                        label={t(
                            'report_waf.view_dialog.date_range_label',
                            'Select Date Range'
                        )}
                        placeholder={t(
                            'report_waf.view_dialog.date_range_placeholder',
                            'Choose start and end dates'
                        )}
                    />

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            type="button"
                            disabled={isLoading}
                        >
                            {t('cancel')}
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={isLoading}
                        >
                            {isLoading
                                ? t('view_dialog.processing')
                                : t('view_dialog.confirm')}
                        </Button>
                    </div>
                </div>
            </form>
        </Dialog>
    )
}

export default ViewReportDialog
